import { Building2, CalendarClock, ChevronDown, ChevronRight, CreditCard, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ElectronicInvoice } from "@fiscalo/shared-types";
import { isConsumerFinalReceiver } from "@fiscalo/shared-types";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";

import {
  buildInvoiceListActions,
  formatComprobanteLabel,
  formatTotal,
  invoiceHasAdjustments,
  isCreditNote,
  isDebitNote,
  resolveConsumerFinalName,
  resolveInvoiceDisplayStatus,
  statusIcon,
  statusVariant,
  type ChildKind,
  type ReissueRef,
} from "@/pages/invoices/invoice-list-shared";

type InvoiceMobileCardProps = {
  invoice: ElectronicInvoice;
  /** When true, the card is a child of a previous row (NC/ND or rejected ancestor). */
  isChild?: boolean;
  childKind?: ChildKind;
  reissueRef?: ReissueRef;
  /** True when this primary row has a rejected ancestor that can be revealed. */
  hasRejectedAncestor?: boolean;
  rejectedAncestorCount?: number;
  isAncestorExpanded?: boolean;
  onToggleRejectedAncestor?: () => void;
  invoicingBlocked: boolean;
  onQueueSubmission?: (invoiceId: string) => void;
  queueSubmissionLoadingId?: string | null;
  onReissueRejected?: (invoiceId: string, cfeType?: string, referencedInvoiceId?: string) => void;
  reissueLoadingId?: string | null;
  onDeleteDraft?: (invoiceId: string) => void;
  deleteDraftLoadingId?: string | null;
};

export function InvoiceMobileCard({
  invoice: inv,
  isChild = false,
  childKind,
  reissueRef,
  hasRejectedAncestor = false,
  rejectedAncestorCount = 0,
  isAncestorExpanded = false,
  onToggleRejectedAncestor,
  invoicingBlocked,
  onQueueSubmission,
  queueSubmissionLoadingId,
  onReissueRejected,
  reissueLoadingId,
  onDeleteDraft,
  deleteDraftLoadingId,
}: InvoiceMobileCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const displayStatus = resolveInvoiceDisplayStatus(inv);
  const StatusIc = statusIcon(displayStatus);
  const listLabel =
    t(`invoices.list.statusLabel.${displayStatus}`, displayStatus) ||
    t(`invoices.status.${displayStatus}`, displayStatus);
  const isRejected = displayStatus === "rejected";
  const receiverRut = inv.receiverSnapshot?.rut;
  const receiverRutOrDoc = inv.receiverSnapshot?.docRecepExt?.trim()
    ? inv.receiverSnapshot.docRecepExt
    : receiverRut?.trim() && receiverRut !== "000000000000"
      ? receiverRut
      : undefined;
  const isConsumerFinal = isConsumerFinalReceiver(receiverRut) && !inv.customerId;
  const consumerFinalName = isConsumerFinal
    ? resolveConsumerFinalName(inv.receiverSnapshot?.name)
    : null;
  const clientDisplayName = (inv as unknown as Record<string, unknown>).clientDisplayName as string | undefined;
  const hasAdjustments = invoiceHasAdjustments(inv);
  const rowActions = buildInvoiceListActions(inv, {
    t,
    navigate,
    invoicingBlocked,
    onQueueSubmission,
    queueSubmissionLoadingId,
    onReissueRejected,
    reissueLoadingId,
    onDeleteDraft,
    deleteDraftLoadingId,
  });

  const isRejectedAncestor = childKind === "rejectedAncestor";
  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isChild && "ml-4 bg-muted/30",
        isRejectedAncestor && "opacity-60"
      )}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/invoices/${inv.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/invoices/${inv.id}`);
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {isChild && (
              <span className="text-muted-foreground" aria-hidden>{"↳"}</span>
            )}
            {isCreditNote(inv.cfeType) && (
              <Badge variant="secondary" className="shrink-0 text-xs font-medium">
                {t("invoices.list.creditNoteShort", "NC")}
              </Badge>
            )}
            {isDebitNote(inv.cfeType) && (
              <Badge variant="secondary" className="shrink-0 text-xs font-medium">
                {t("invoices.list.debitNoteShort", "ND")}
              </Badge>
            )}
            <span className="font-medium text-foreground">
              {formatComprobanteLabel(inv.cfeType, inv.internalNumber, t)}
            </span>
            {/* Mirror of invoices-page — the narrow viewport must not drop the
                marca, or the same list says two different things. */}
            {(inv as unknown as Record<string, unknown>).brandName ? (
              <Badge variant="outline" className="shrink-0 text-xs font-medium">
                {String((inv as unknown as Record<string, unknown>).brandName)}
              </Badge>
            ) : null}
            {hasAdjustments && (
              <span className="text-xs text-muted-foreground">
                {t("invoices.list.adjusted", "Adjusted")}
              </span>
            )}
            {/* Mirror of invoices-page: hide the successor chip once the
               retry is "Enviada" — the green badge + the "Ver intento
               rechazado" toggle below already make the chain obvious. */}
            {reissueRef &&
              !(
                reissueRef.direction === "successorOf" &&
                displayStatus === "sent"
              ) && (
              <button
                type="button"
                data-interactive
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/invoices/${reissueRef.targetId}`);
                }}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
                <span>
                  {reissueRef.direction === "successorOf"
                    ? t("invoices.list.reissueOf", "Reemite {{label}}", {
                        label: formatComprobanteLabel(
                          reissueRef.targetCfeType,
                          reissueRef.targetInternalNumber,
                          t
                        ),
                      })
                    : t("invoices.list.reissuedBy", "Reemitida por {{label}}", {
                        label: formatComprobanteLabel(
                          reissueRef.targetCfeType,
                          reissueRef.targetInternalNumber,
                          t
                        ),
                      })}
                </span>
              </button>
            )}
            {hasRejectedAncestor && onToggleRejectedAncestor && (
              <button
                type="button"
                data-interactive
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleRejectedAncestor();
                }}
                className="inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {isAncestorExpanded ? (
                  <ChevronDown className="h-3 w-3" aria-hidden />
                ) : (
                  <ChevronRight className="h-3 w-3" aria-hidden />
                )}
                {isAncestorExpanded
                  ? rejectedAncestorCount === 1
                    ? t("invoices.list.hideRejectedAttempt", "Ocultar intento")
                    : t("invoices.list.hideRejectedAttempts", "Ocultar intentos")
                  : rejectedAncestorCount === 1
                    ? t("invoices.list.viewRejectedAttempt", "Ver intento rechazado")
                    : t("invoices.list.viewRejectedAttemptsCount", "Ver {{count}} intentos rechazados", { count: rejectedAncestorCount })}
              </button>
            )}
          </div>
          <p className="text-sm text-muted-foreground capitalize">
            {inv.paymentMethod === "credito"
              ? t("invoices.list.paymentMethodCredito", "Credit")
              : t("invoices.list.paymentMethodContado", "Spot")}
          </p>
          <div className="min-w-0">
            {isRejected ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex min-w-0 max-w-full">
                    <StatusBadge
                      variant={statusVariant(displayStatus)}
                      className="inline-flex max-w-full"
                    >
                      <StatusIc className="h-3 w-3 shrink-0" aria-hidden />
                      <span className="min-w-0 truncate">{listLabel}</span>
                    </StatusBadge>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px]">
                  {t(
                    "invoices.list.rejectedBadgeTooltip",
                    "This invoice was rejected by DGI and has no fiscal effect. A new invoice must be issued."
                  )}
                </TooltipContent>
              </Tooltip>
            ) : (
              <StatusBadge
                variant={statusVariant(displayStatus)}
                className="inline-flex max-w-full"
              >
                <StatusIc className="h-3 w-3 shrink-0" aria-hidden />
                <span className="min-w-0 truncate">{listLabel}</span>
              </StatusBadge>
            )}
            {inv.createdVia === "deel" && (
              <StatusBadge variant="info" className="ml-2 inline-flex w-fit">
                <Building2 className="h-3 w-3 shrink-0" aria-hidden />
                <span>{t("invoices.list.createdByDeel", "Deel")}</span>
              </StatusBadge>
            )}
            {inv.createdVia === "subscription" && (
              <StatusBadge variant="info" className="ml-2 inline-flex w-fit">
                <CreditCard className="h-3 w-3 shrink-0" aria-hidden />
                <span>{t("invoices.list.createdBySubscription", "Fiscalo subscription")}</span>
              </StatusBadge>
            )}
            {inv.status === "draft" && inv.scheduledSendAt && (
              <div className="mt-1 flex flex-col gap-1">
                <StatusBadge variant="info" className="inline-flex w-fit">
                  <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                  <span>{t("invoices.schedule.scheduled", "Comprobante programado")}</span>
                </StatusBadge>
                <span className="text-xs text-muted-foreground">
                  {t("invoices.schedule.scheduledFor", "Programada para {{dateTime}}", {
                    dateTime: formatDisplayDate(inv.scheduledSendAt, {
                      includeTime: true,
                      locale: i18n.language,
                    }),
                  })}
                </span>
              </div>
            )}
          </div>
          <p className="text-lg font-semibold tabular-nums text-foreground">
            {formatTotal(inv.totals?.total?.currency, inv.totals?.total?.amount)}
          </p>
          <div className="text-sm">
            {isConsumerFinal ? (
              <>
                <span className={consumerFinalName ? "font-medium text-foreground" : undefined}>
                  {consumerFinalName ??
                    t("invoices.list.consumerFinal", "Consumer final")}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {consumerFinalName
                    ? t(
                        "invoices.list.consumerFinalNamedHelp",
                        "Consumer final - no fiscal identification required"
                      )
                    : t(
                        "invoices.list.consumerFinalHelp",
                        "No fiscal identification required"
                      )}
                </span>
              </>
            ) : (
              <>
                <span className="font-medium text-foreground">
                  {clientDisplayName || inv.receiverSnapshot?.name || "—"}
                </span>
                {receiverRutOrDoc &&
                  receiverRutOrDoc !== "000000000000" && (
                  <span className="mt-0.5 block text-xs text-muted-foreground tabular-nums">
                    {receiverRutOrDoc}
                  </span>
                )}
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {inv.issueDate
              ? formatDisplayDate(inv.issueDate, {
                  locale: i18n.language,
                  useCalendarDate: true,
                })
              : "—"}
          </p>
        </div>
        <div
          className="shrink-0"
          data-interactive
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TableActionsMenu actions={rowActions} />
        </div>
      </div>
    </Card>
  );
}
